<script setup lang="ts">
import { useI18n } from "vue-i18n";
import { useRoute, useRouter } from "vue-router";
import { computed, ref, onMounted, watch } from "vue";
import useArray from "@/composable/components/array";
import services from "@/composable/services";
import { LocalApplicationResult } from "@/types/front/LocalApplicationResult";
import {
  Configuration,
  RightsRequestResult,
  User,
} from "@/types/generated/UsedGeneratedTypes";
import { BButton, BCheckbox, BCollapse, BField, BIcon, BInput, BTooltip } from "buefy";
import LoadingAnimate from "@/components/common/LoadingAnimate.vue";
import InfoRightRequest from "@/components/common/authorization/InfoRightRequest.vue";
import SubMenu from "@/components/common/SubMenu.vue";
import TitleAndDescription from "@/components/common/TitleAndDescription.vue";
import PageView from "@/views/common/PageView.vue";
import ListAuthorisation from "@/components/common/authorization/ListAuthorisation.vue";
import { LOCAL_STORAGE_LANG } from "@/services/Fetcher";
import { getListColumnName } from "@/composable/authorization/grantableInfos";
import { useDataTypeAuthorizations } from "@/composable/authorization/useDataTypeAuthorizations";
import { SubMenuPath } from "@/types/ui/SubMenuPath";
import { escapeHtml } from "@/composable/components/highlight";
import type { GetAuthorizationResult } from "@/types/generated/UsedGeneratedTypes";

const { t, locale: i18nLocale } = useI18n();
const router = useRouter();
const route = useRoute();
const props = defineProps<{
  applicationName: string;
  authorizationId: string;
}>();

// Mode consultation seule : activé quand l'URL porte la query `?readOnly=1`,
// typiquement lors d'un click sur l'icône oeil d'une demande déjà traitée
// dans le listing. Désactive toutes les interactions du bloc "Traitement".
const isReadOnly = computed(() => String(route.query.readOnly ?? "") === "1");

// État réactif des deux collapses ( détail / traitement ). En lecture
// seule on replie le bloc "Détail de la demande" car les informations
// importantes ( décision, autorisations attribuées, mail ) sont dans
// le bloc "Demande traitée" - on évite que le gestionnaire scrolle.
// Bound via v-model pour permettre un controle réactif initial fiable
// ( :open seul ne suffit pas avec certaines versions de Buefy ).
const isDetailCollapseOpen = ref(!isReadOnly.value);
const isTreatmentCollapseOpen = ref(true);

const application = ref(new LocalApplicationResult() as LocalApplicationResult);
const isLoading = ref(false);
const configuration = ref({} as Configuration);
const grantableUsers = ref<User[]>([]);
const currentAuthorization = ref({} as RightsRequestResult);
const referencesScopes = ref({});
const initialized = ref(false);
const comment = ref("");
const textMail = ref("");
const mailSubject = ref("");
const datatypes = ref({ withScope: {}, withoutScope: {} });
const listColumnName = getListColumnName();
const { shallowRefArray: subMenuPaths, doChangeArray: changeSubMenuPaths } = useArray();

// Identifiant lisible du demandeur. Tant que la requête n'est pas chargée,
// on affiche l'UUID brut pour rester déterministe ; dès que les utilisateurs
// granteables sont disponibles, on retombe sur le login.
const requesterLogin = computed(() => {
  const userId = currentAuthorization.value.user;
  if (!userId) return "";
  const found = grantableUsers.value.find(u => u.id === userId);
  return found?.label || userId;
});

// Email du demandeur , résolu côté serveur ( cf. RightsRequestResult.userEmail ).
// Affiché à droite du label "Texte du mail à transmettre à l'utilisateur" pour
// que le gestionnaire sache exactement à qui le mail sera envoyé.
const requesterEmail = computed(() => currentAuthorization.value?.userEmail || "");

// La sélection des autorisations cochables s'appuie sur ListAuthorisation
// qui attend un objet { name } correspondant au login concerné par la
// demande. Pour le traitement, l'utilisateur cible est le demandeur.
const row = computed(() => ({ name: requesterLogin.value }));

const hasDependencies = computed(() => {
  const result = new Set();
  const authorizations = currentAuthorization.value.rightsRequest;
  const hierarchicalNodes = configuration.value.hierarchicalNodes;

  if (authorizations && hierarchicalNodes) {
    for (const [datatype, auth] of Object.entries(authorizations)) {
      if (auth[0].operationTypes?.length > 0) {
        const node = hierarchicalNodes.find(n => n.nodeName === datatype);
        if (node?.depends?.length > 0) {
          node.depends.forEach(dep => result.add(dep));
        }
      }
    }
  }
  return Array.from(result);
});

// useDataTypeAuthorizations fournit la liste des autorisations existantes
// de l'application + les utilitaires de filtrage / modal. dataTypeId n'est
// pas pertinent ici (on liste TOUTES les autorisations applicables) mais
// le composable l'attend dans son interface ; on passe l'authorizationId
// qui est déjà disponible et inoffensif.
const {
  listAuthorizations,
  resultFilterAuthByName,
  checkbox,
  updateListUsersInAuthorization,
  filterListAuthName: doFilterListAuthName,
} = useDataTypeAuthorizations({
  dataTypeId: props.authorizationId,
  applicationName: props.applicationName,
});

const isRejected = computed(() => !!checkbox.REJECTED);
const isNoMail = computed(() => !!checkbox.NO_MAIL);
const showNoNotificationWarning = computed(() => isRejected.value && isNoMail.value);
// Le texte du mail est obligatoire dès lors que l'envoi de mail est actif
// ( case "Ne pas envoyer d'email" décochée ) , que la demande soit acceptée
// ou refusée. Si on n'envoie pas de mail, le champ devient optionnel et
// le cadre rouge disparaît.
const isMailRequired = computed(() => !isNoMail.value);
// Cadre rouge uniquement quand un mail est requis ET vide.
const isMailMissing = computed(() => isMailRequired.value && !textMail.value.trim());

// Format de date localisé ( jj/mm/aaaa hh:mm en FR, mm/dd/yyyy hh:mm AM/PM en EN ).
// Évite l'affichage de l'ISO brut ( 2026-05-07T00:44:11.288Z ).
const formattedCreateDate = computed(() => {
  const raw = currentAuthorization.value.createDate;
  if (!raw) return "";
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) return String(raw);
  return new Intl.DateTimeFormat(i18nLocale.value || "fr", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
});

const init = async () => {
  isLoading.value = true;
  try {
    const app = await services.applicationService.getApplication(props.applicationName, ["DATATYPE", "CONFIGURATION"]);
    application.value = app;
    configuration.value = app?.configuration;

    const request = await services.requestRightsService.getRightsRequests(props.applicationName, {
      uuids: [props.authorizationId],
    });

    if (request.rightsRequests?.[0]) {
      currentAuthorization.value = request.rightsRequests[0];
    }
    grantableUsers.value = (request.users || []) as User[];

    // Pré-remplissage des champs de traitement à partir des données persistées
    // côté backend ( utile en consultation lecture seule, ainsi qu'en cas de
    // re-traitement futur ). On les copie une seule fois ici ; les watchers
    // du formulaire continueront à fonctionner normalement ensuite.
    const treatmentPayload = currentAuthorization.value;
    if (treatmentPayload?.setted) {
      comment.value = treatmentPayload.treatmentComment ?? "";
      textMail.value = treatmentPayload.treatmentMailBody ?? "";
      mailSubject.value = treatmentPayload.treatmentMailSubject ?? "";
      checkbox.REJECTED = treatmentPayload.treatmentDecision === "REJECTED";
      // suppressMail = vrai si la demande a été traitée sans mail enregistré
      checkbox.NO_MAIL = !treatmentPayload.treatmentMailBody;
    }

    datatypes.value = {
      withScope: Object.keys(application.value.dataTypes)
        .filter(name => configuration.value.dataDescription[name].authorization)
        .reduce((acc: any, dataType: string) => {
          acc[dataType] = {
            id: dataType,
            name:
                  services.internationalisationService.localeReferenceNames(dataType, application.value)
                  || dataType,
          };
          return acc;
        }, {}),
      withoutScope: Object.keys(application.value.dataTypes)
        .filter(name => configuration.value.dataDescription[name].authorization === null)
        .reduce((acc: any, dataType: string) => {
          acc[dataType] = {
            id: dataType,
            name:
                  services.internationalisationService.localeReferenceNames(dataType, application.value)
                  || dataType,
          };
          return acc;
        }, {}),
    };

    initialized.value = true;
  }
  catch (e) {
    console.error(e);
  }
  finally {
    isLoading.value = false;
  }
};

onMounted(async () => {
  await init();
  changeSubMenuPaths([
    new SubMenuPath(
      t("requestAuthorization.list-request"),
      () => {
        router.push(
          `/applications/${props.applicationName}/authorizationsRequest`,
        );
      },
      () => {
        router.push(`/applications/${props.applicationName}/authorizationsRequest`);
      },
    ),
    new SubMenuPath(
      t("requestAuthorization.treatment-request"),
      () => {},
      () => {
        router.push(`/applications/${props.applicationName}/authorizationsRequest`);
      },
    ),
  ]);
});

const lang = localStorage.getItem(LOCAL_STORAGE_LANG)
  || application.value.configuration?.applicationDescription?.defaultLanguage
  || "fr";

// Liste des autorisations cochées pour le demandeur, dérivée de l'objet
// réactif `checkbox` exposé par useDataTypeAuthorizations. Déclarée tôt
// pour que les computed templates ci-dessous puissent y accéder sans
// trigger de TDZ ( le watch [generatedTemplateBody, ...] avec
// immediate=true évalue les computed dès le setup ).
const selectedAuthorizations = computed<GetAuthorizationResult[]>(() => {
  if (!requesterLogin.value) return [];
  const suffix = "_" + requesterLogin.value;
  return (listAuthorizations.value as GetAuthorizationResult[]).filter(
    auth => checkbox[auth.name + suffix],
  );
});

// Liste des autorisations effectivement affichées dans la section traitement.
// En lecture seule, on ne montre QUE celles qui ont été appliquées au
// demandeur ( linkedAuthorizationIds persistées ) ; le filtre interactif
// est inopérant. En mode édition, on conserve le filtrage par recherche
// utilisateur via resultFilterAuthByName.
const displayedAuthorizations = computed<GetAuthorizationResult[]>(() => {
  if (!isReadOnly.value) {
    return resultFilterAuthByName.value as GetAuthorizationResult[];
  }
  const linkedIds = currentAuthorization.value?.linkedAuthorizationIds ?? [];
  if (linkedIds.length === 0) return [];
  const linkedSet = new Set(linkedIds);
  return (listAuthorizations.value as GetAuthorizationResult[])
    .filter(auth => auth.uuid && linkedSet.has(auth.uuid));
});

// Placeholders communs aux templates ( sujet + corps ).
const templatePlaceholders = computed(() => ({
  requesterLogin: requesterLogin.value || "",
  applicationName: application.value?.localName || application.value?.title || props.applicationName,
}));
const hasSelectedAuthorization = computed(() => selectedAuthorizations.value.length > 0);

/**
 * Sélecteur de template internationalisé selon la décision courante :
 *   - REJECTED  → key = `rejectKey`
 *   - APPROVED + au moins 1 auth cochée → key = `approveKey`
 *   - sinon ( aucune décision exploitable ) → chaîne vide.
 * Centralisé pour rester cohérent entre sujet et corps du mail.
 */
function pickMailTemplate(rejectKey: string, approveKey: string): string {
  if (isRejected.value) return t(rejectKey, templatePlaceholders.value);
  if (hasSelectedAuthorization.value) return t(approveKey, templatePlaceholders.value);
  return "";
}

const generatedTemplateBody = computed(
  () => pickMailTemplate("requestAuthorization.reject-mail-template", "requestAuthorization.approve-mail-template"),
);
const generatedMailSubject = computed(
  () => pickMailTemplate("requestAuthorization.reject-mail-subject-template", "requestAuthorization.approve-mail-subject-template"),
);

/**
 * Lie un champ texte ( ref ) à un template auto-généré ( ref / computed ) :
 *   - synchronise la valeur du champ avec le template tant que le contenu
 *     courant est vide ou égal au dernier template injecté ;
 *   - préserve toute saisie manuelle du gestionnaire ;
 *   - expose un setter `clear()` pour vider le champ + reset du tracker
 *     ( utilisé par la case "Ne pas envoyer mail" ).
 *
 * Factorisation pour appliquer la même logique à textMail et mailSubject
 * sans dupliquer le pattern lastGenerated / watch / immediate.
 */
function useTemplateAutoFill(currentRef: typeof textMail, generatedRef: typeof generatedTemplateBody) {
  let lastGenerated = "";
  watch([generatedRef, isRejected, i18nLocale, requesterLogin], () => {
    if (isReadOnly.value) return;
    if (!currentRef.value || currentRef.value === lastGenerated) {
      currentRef.value = generatedRef.value;
      lastGenerated = generatedRef.value;
    }
  }, { immediate: true });
  return {
    clear() {
      currentRef.value = "";
      lastGenerated = "";
    },
  };
}

const mailBodyAutoFill = useTemplateAutoFill(textMail, generatedTemplateBody);
const mailSubjectAutoFill = useTemplateAutoFill(mailSubject, generatedMailSubject);

// Décochage des autorisations sélectionnées dès que le gestionnaire active
// le refus. Séparé de l'auto-fill du mail pour respecter la séparation
// des responsabilités ( fill = champ texte, uncheck = état du checkbox map ).
watch(isRejected, (rejected) => {
  if (isReadOnly.value || !rejected) return;
  const suffix = "_" + requesterLogin.value;
  for (const key of Object.keys(checkbox)) {
    if (key.endsWith(suffix)) {
      checkbox[key] = false;
    }
  }
});

// Activation de "Ne pas envoyer d'email" : sujet + corps du mail vidés.
// Le tracker interne de chaque auto-fill est aussi reset pour permettre
// une ré-injection propre du template si la case est décochée plus tard.
watch(isNoMail, (noMail) => {
  if (isReadOnly.value) return;
  if (noMail) {
    mailBodyAutoFill.clear();
    mailSubjectAutoFill.clear();
  }
});

// Restaure l'état des cases d'autorisations à partir des linkedAuthorizationIds
// persistés côté backend, dès que la liste des autorisations de l'application
// devient disponible ( chargée de manière asynchrone par
// useDataTypeAuthorizations ). Une seule application suffit : on s'arrête
// après le premier mappage réussi pour ne pas écraser une saisie ultérieure.
let linkedAuthsApplied = false;
watch([listAuthorizations, requesterLogin], () => {
  if (linkedAuthsApplied) return;
  const linkedIds = currentAuthorization.value?.linkedAuthorizationIds;
  const login = requesterLogin.value;
  if (!login || !linkedIds || linkedIds.length === 0) return;
  if (!listAuthorizations.value || listAuthorizations.value.length === 0) return;

  const linkedSet = new Set(linkedIds);
  for (const auth of listAuthorizations.value as GetAuthorizationResult[]) {
    if (auth.uuid && linkedSet.has(auth.uuid)) {
      checkbox[auth.name + "_" + login] = true;
    }
  }
  linkedAuthsApplied = true;
});

/**
 * Récapitulatif HTML affiché dans la modale de confirmation. Le contenu est
 * monté à la volée à partir de l'état du formulaire ; toute valeur saisie
 * par l'utilisateur (commentaire, texte mail, nom d'autorisation) est
 * échappée via {@link escapeHtml} pour éviter toute injection HTML/XSS via
 * la directive `v-html` du composant {@code DialogService}.
 */
function buildRecapHtml(): string {
  const lines: string[] = [];

  const decisionLabel = isRejected.value
    ? t("requestAuthorization.recap-decision-rejected")
    : t("requestAuthorization.recap-decision-approved");
  lines.push(`<p><b>${escapeHtml(t("requestAuthorization.recap-decision"))} :</b> ${escapeHtml(decisionLabel)}</p>`);

  if (!isRejected.value) {
    if (selectedAuthorizations.value.length === 0) {
      lines.push(`<p><b>${escapeHtml(t("requestAuthorization.recap-selected-auths"))} :</b> <em>${escapeHtml(t("requestAuthorization.recap-no-auth-selected"))}</em></p>`);
    }
    else {
      const items = selectedAuthorizations.value
        .map(a => `<li>${escapeHtml(a.name)}</li>`)
        .join("");
      lines.push(`<p><b>${escapeHtml(t("requestAuthorization.recap-selected-auths"))} :</b></p><ul>${items}</ul>`);
    }
  }

  const commentValue = comment.value.trim();
  if (commentValue) {
    lines.push(`<p><b>${escapeHtml(t("requestAuthorization.recap-comment"))} :</b><br/>${escapeHtml(commentValue).replace(/\n/g, "<br/>")}</p>`);
  }
  else {
    lines.push(`<p><b>${escapeHtml(t("requestAuthorization.recap-comment"))} :</b> <em>${escapeHtml(t("requestAuthorization.recap-comment-empty"))}</em></p>`);
  }

  if (isNoMail.value) {
    lines.push(`<p><b>${escapeHtml(t("requestAuthorization.recap-mail"))} :</b> <em>${escapeHtml(t("requestAuthorization.recap-mail-disabled"))}</em></p>`);
  }
  else {
    const subjectValue = mailSubject.value.trim();
    if (subjectValue) {
      lines.push(`<p><b>${escapeHtml(t("requestAuthorization.mail-subject"))} :</b> ${escapeHtml(subjectValue)}</p>`);
    }
    const mailValue = textMail.value.trim();
    if (mailValue) {
      lines.push(`<p><b>${escapeHtml(t("requestAuthorization.recap-mail"))} :</b><br/>${escapeHtml(mailValue).replace(/\n/g, "<br/>")}</p>`);
    }
    else {
      lines.push(`<p><b>${escapeHtml(t("requestAuthorization.recap-mail"))} :</b> <em>${escapeHtml(t("requestAuthorization.recap-mail-empty"))}</em></p>`);
    }
  }

  return lines.join("");
}

function navigateBackToList() {
  router.push(`/applications/${props.applicationName}/authorizationsRequest`);
}

const handleBack = () => {
  // En lecture seule, aucune saisie à perdre : on retourne directement
  // au listing sans demander de confirmation.
  if (isReadOnly.value) {
    navigateBackToList();
    return;
  }
  services.alertService.confirm(
    t("requestAuthorization.back-confirm-title"),
    t("requestAuthorization.back-confirm-message"),
    t("requestAuthorization.back-confirm-yes"),
    "is-warning",
    t("alert.cancel"),
    navigateBackToList,
  );
};

const handleValidate = () => {
  // Pré-validation côté UI : un envoi de mail actif requiert un contenu non vide.
  if (isMailRequired.value && !textMail.value.trim()) {
    services.alertService.toastError(t("requestAuthorization.error-mail-required-on-reject"));
    return;
  }

  services.alertService.confirm(
    t("requestAuthorization.validate-confirm-title"),
    buildRecapHtml(),
    t("requestAuthorization.validate-confirm-yes"),
    "is-success",
    t("alert.cancel"),
    submitTreatment,
  );
};

async function submitTreatment() {
  isLoading.value = true;
  try {
    await services.requestRightsService.treatRequest(
      props.applicationName,
      props.authorizationId,
      {
        status: isRejected.value ? "REJECTED" : "APPROVED",
        linkedAuthorizationIds: isRejected.value
          ? []
          : selectedAuthorizations.value
              .map(a => a.uuid)
              .filter((id): id is string => !!id),
        treatmentComment: comment.value,
        mailSubject: mailSubject.value,
        mailBody: textMail.value,
        suppressMail: isNoMail.value,
      },
    );
    services.alertService.toastSuccess(t("alert.modified-request"));
    navigateBackToList();
  }
  catch (e) {
    services.alertService.toastServerError(e as Parameters<typeof services.alertService.toastServerError>[0]);
  }
  finally {
    isLoading.value = false;
  }
}
</script>

<template>
  <PageView
    :application="application"
    class="with-submenu"
  >
    <SubMenu
      :aria-label="$t('menu.aria-sub-menu')"
      :paths="subMenuPaths"
      :root="application?.localName || application?.title"
      role="navigation"
    />

    <TitleAndDescription
      :application="application"
      :local-title="$t('dataTypeAuthorizations.title', { user: requesterLogin })"
      :local-description="currentAuthorization.comment"
      :local-name="application.localName || application.title"
    />

    <LoadingAnimate
      v-if="isLoading"
      :size="'is-medium'"
    />

    <div
      v-else
      class="columns is-multiline"
    >
      <div
        v-if="isReadOnly"
        class="column is-12 read-only-banner"
        role="status"
      >
        <BIcon icon="lock" size="is-small" />
        <span>{{ $t('requestAuthorization.read-only-banner') }}</span>
      </div>
      <div class="column is-12">
        <BCollapse
          v-model="isDetailCollapseOpen"
          class="card"
          animation="slide"
          aria-id="detailRequestCollapse"
          style="padding: 0; "
        >
          <template #trigger="props">
            <div
              class="card-header request"
              role="button"
              aria-controls="detailRequestCollapse"
              :aria-expanded="props.open"
            >
              <p class="card-header-title">
                {{ $t("requestAuthorization.detail-request") }}
              </p>
              <a class="card-header-icon">
                <BIcon :icon="props.open ? 'chevron-up' : 'chevron-down'" />
              </a>
            </div>
          </template>
          <div class="card-content">
            <div class="content">
              <InfoRightRequest
                :user="requesterLogin"
                :date="formattedCreateDate"
                :comment="currentAuthorization.comment || ''"
                :application="application"
                :application-name="application.localName || application.title"
                :authorization-id="authorizationId"
                :authorizations="currentAuthorization.rightsRequest"
                :current-authorization="currentAuthorization"
                :datatypes="datatypes"
                :has-dependencies="hasDependencies"
                :initialized="true"
                :references-scopes="referencesScopes"
              />
            </div>
          </div>
        </BCollapse>
      </div>

      <div class="column is-12">
        <BCollapse
          v-model="isTreatmentCollapseOpen"
          class="card"
          animation="slide"
          aria-id="treatmentRequestCollapse"
          style="padding: 0"
        >
          <template #trigger="props">
            <div
              class="card-header treatment"
              role="button"
              aria-controls="treatmentRequestCollapse"
              :aria-expanded="props.open"
            >
              <p class="card-header-title">
                {{ isReadOnly
                  ? $t("requestAuthorization.treatment-request-readonly")
                  : $t("requestAuthorization.treatment-request") }}
              </p>
              <a class="card-header-icon">
                <BIcon :icon="props.open ? 'chevron-up' : 'chevron-down'" />
              </a>
            </div>
          </template>
          <div class="card-content">
            <div class="content">
              <BField
                :label="isReadOnly
                  ? $t('requestAuthorization.select-auth-readonly')
                  : $t('requestAuthorization.select-auth')"
                :aria-label="isReadOnly
                  ? $t('requestAuthorization.select-auth-readonly')
                  : $t('requestAuthorization.select-auth')"
              >
                <span v-if="!isReadOnly">
                  {{ $t('dataTypeAuthorizations.filter-auth-by-name') }}
                </span>
                <b-input
                  v-if="!isReadOnly"
                  :placeholder="$t('dataTypeAuthorizations.search')"
                  :aria-label="$t('dataTypeAuthorizations.search')"
                  icon="search"
                  type="search"
                  rounded
                  :disabled="isReadOnly"
                  @input="doFilterListAuthName(listAuthorizations, $event)"
                />
              </BField>
              <div class="columns is-multiline auth-list-wrapper">
                <ListAuthorisation
                  :lang="lang"
                  :result-filter-auth-by-name="displayedAuthorizations"
                  :checkbox="checkbox"
                  :row="row"
                  :list-column-name="listColumnName"
                  :is-user-manager="true"
                  :disabled="isRejected || isReadOnly"
                  :application="application"
                  :update-list-users-in-authorization="updateListUsersInAuthorization"
                />
              </div>
              <BCheckbox
                v-if="!isReadOnly"
                v-model="checkbox['REJECTED']"
                :disabled="isReadOnly"
              >
                {{ $t('requestAuthorization.rejected') }}
              </BCheckbox>
              <BField
                :label="isReadOnly
                  ? $t('requestAuthorization.text-comment-readonly')
                  : $t('requestAuthorization.text-comment')"
                :aria-label="isReadOnly
                  ? $t('requestAuthorization.text-comment-readonly')
                  : $t('requestAuthorization.text-comment')"
              >
                <BInput
                  v-model="comment"
                  type="textarea"
                  rows="4"
                  maxlength="800"
                  :disabled="isReadOnly"
                />
              </BField>
              <BField
                :aria-label="isReadOnly
                  ? $t('requestAuthorization.mail-subject-readonly')
                  : $t('requestAuthorization.mail-subject')"
              >
                <template #label>
                  <div class="text-mail-label">
                    <span>{{ isReadOnly
                      ? $t('requestAuthorization.mail-subject-readonly')
                      : $t('requestAuthorization.mail-subject') }}</span>
                    <span
                      v-if="requesterEmail"
                      class="recipient-email"
                      :title="requesterEmail"
                    >
                      <BIcon icon="envelope" size="is-small" />
                      {{ requesterEmail }}
                    </span>
                  </div>
                </template>
                <BInput
                  v-model="mailSubject"
                  type="text"
                  maxlength="200"
                  :disabled="isNoMail || isReadOnly"
                />
              </BField>
              <BField
                :label="isReadOnly
                  ? $t('requestAuthorization.text-mail-readonly')
                  : $t('requestAuthorization.text-mail')"
                :aria-label="isReadOnly
                  ? $t('requestAuthorization.text-mail-readonly')
                  : $t('requestAuthorization.text-mail')"
                :class="{ 'mail-required': isMailMissing && !isReadOnly }"
              >
                <BInput
                  v-model="textMail"
                  type="textarea"
                  rows="4"
                  maxlength="800"
                  :disabled="isNoMail || isReadOnly"
                />
              </BField>
              <div class="no-mail-row">
                <BCheckbox
                  v-model="checkbox['NO_MAIL']"
                  :disabled="isReadOnly"
                >
                  {{ isReadOnly
                    ? $t('requestAuthorization.no-mail-readonly')
                    : $t('requestAuthorization.no-mail') }}
                </BCheckbox>
                <span
                  v-if="showNoNotificationWarning"
                  class="no-mail-warning"
                  role="alert"
                >
                  <BIcon icon="alert" size="is-small" />
                  {{ $t('requestAuthorization.warn-no-notification-on-reject') }}
                </span>
              </div>
            </div>
          </div>
        </BCollapse>
        <div class="buttons is-right">
          <BTooltip
            :label="$t('requestAuthorization.back-tooltip')"
            position="is-top"
            multilined
          >
            <BButton
              type="is-warning"
              :loading="isLoading"
              :aria-label="$t('requestAuthorization.back-tooltip')"
              @click="handleBack"
            >
              {{ $t('requestAuthorization.back') }}
            </BButton>
          </BTooltip>
          <BTooltip
            v-if="!isReadOnly"
            :label="$t('requestAuthorization.validate-tooltip')"
            position="is-top"
            multilined
          >
            <BButton
              type="is-success"
              :loading="isLoading"
              :aria-label="$t('requestAuthorization.validate-tooltip')"
              @click="handleValidate"
            >
              {{ $t('requestAuthorization.validate-treatment') }}
            </BButton>
          </BTooltip>
        </div>
      </div>
    </div>
  </PageView>
</template>

<style scoped lang="scss">
.card {
  margin-bottom: 1.5rem;
}
.request {
  .card-header {
    background-color: $color-success-02;
    .card-header-title {
      font-weight: bold !important;
    }
  }
}
.treatment {
  .card-header {
    background-color: $color-warning-02;
    .card-header-title {
      font-weight: bold !important;
    }
  }
}
.auth-list-wrapper {
  margin-top: 0.5rem;
  margin-bottom: 1rem;
}
.no-mail-row {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}
.no-mail-warning {
  color: #b8392c;
  font-size: 0.9rem;
  font-style: italic;
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
}
// Header du champ "Texte du mail" : libellé à gauche, adresse mail du
// demandeur à droite ( visuel "envelope" + email ). Aide le gestionnaire
// à confirmer le destinataire avant de valider le traitement.
.text-mail-label {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  width: 100%;
}
.recipient-email {
  font-weight: normal;
  font-size: 0.9rem;
  color: #1f5b9c;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
// Bandeau d'information visible uniquement en mode consultation : indique
// au gestionnaire que la demande est verrouillée ( déjà traitée ).
.read-only-banner {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  border-radius: 0.25rem;
  background-color: #f0f7ff;
  color: #1f5b9c;
  font-weight: 600;
}
// Cadre rouge sur le champ "Texte du mail" lorsqu'il devient obligatoire
// ( refus + envoi de mail actif ). Le ":deep" est nécessaire car le textarea
// rendu par Buefy est encapsulé dans un sous-composant.
.mail-required :deep(textarea) {
  border-color: #b8392c;
  box-shadow: 0 0 0 1px rgba(184, 57, 44, 0.25);
}
</style>
